/**
 * webPlatform — the ONE seam between the forked design editor and Brightivity (brightivity.dev only).
 * Forked from studioCloud's webPlatform.js; every function keeps the desktop's `{ success, ... }` shape
 * so the editor code calling it stays as it was.
 *
 *   saveDesign / updateDesign  → brightivity.dev/library/designs (thumbnail + design JSON in the form;
 *                                the full-size picture streamed to …/designs/:id/original, so a big
 *                                600-dpi export never sits in a worker as a form part)
 *   editorDraft{Save,Load,Clear} → localStorage, one draft per Brightivity user
 *   removeBackground           → brightivity.dev/design/remove-bg (one call: upload, fal, result)
 *   fontCache{Get,Put}         → no-op (the browser HTTP cache already does it)
 */
import { API_BASE } from '../../../config.js';
import { getSession } from '../../../api/client.js';

export function authToken() {
  const s = getSession();
  return (s && s.token) || null;
}
const draftKey = () => { const s = getSession(); return `bv.editorDraft.${(s && s.userId) || 'anon'}`; };

// data:image/png;base64,… → Blob (the editor produces data URLs).
function dataUrlToBlob(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
  const [header, b64] = dataUrl.split(',');
  const mime = /data:([^;]+)/.exec(header)?.[1] || 'image/png';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function designFormData(design, { create }) {
  const form = new FormData();
  form.append('name', design.d_name || 'Untitled design');
  if (create) form.append('source', 'editor');
  const thumb = dataUrlToBlob(design.d_thumbnail);
  if (thumb) form.append('thumbnail', thumb, 'thumbnail.png');
  if (design.d_json) form.append('designJson', JSON.stringify(design.d_json));
  if (design.d_metaData) form.append('metadata', JSON.stringify(design.d_metaData));
  const dims = design.d_metaData?.dimensions;
  if (dims?.width) form.append('width', String(Math.round(dims.width)));
  if (dims?.height) form.append('height', String(Math.round(dims.height)));
  return form;
}

async function send(method, path, body, headers = {}) {
  const token = authToken();
  if (!token) return { success: false, error: 'Not signed in' };
  try {
    const res = await fetch(`${API_BASE}${path}`, { method, headers: { Authorization: `Bearer ${token}`, ...headers }, body });
    const json = await res.json().catch(() => null);
    if (!res.ok || (json && json.success === false)) return { success: false, error: json?.error || `HTTP ${res.status}` };
    return { success: true, design: json?.data || json };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/** The full-size picture, streamed as the raw body (PNG/JPEG). */
async function putOriginal(designId, design) {
  const original = design.d_fullBlob instanceof Blob ? design.d_fullBlob : dataUrlToBlob(design.d_pdf);
  if (!original || !original.size) return { success: true };
  return send('PUT', `/library/designs/${designId}/original`, original, { 'Content-Type': original.type || 'image/png' });
}

/** Create a new library design. Mirrors electronAPI.saveDesign; `design.id` is the id the server made. */
export async function saveDesign(design) {
  const r = await send('POST', '/library/designs', designFormData(design, { create: true }));
  if (!r.success) return r;
  const id = r.design?.id;
  const o = id ? await putOriginal(id, design) : { success: true };
  return o.success ? { ...r, design: o.design || r.design } : { success: false, error: `Saved, but the full picture failed: ${o.error}`, design: r.design };
}

/** Save in place over an existing library design. Mirrors electronAPI.updateDesign. */
export async function updateDesign(designId, patch) {
  const r = await send('PUT', `/library/designs/${designId}`, designFormData(patch, { create: false }));
  if (!r.success) return r;
  const o = await putOriginal(designId, patch);
  return o.success ? { ...r, design: o.design || r.design } : { success: false, error: `Saved, but the full picture failed: ${o.error}` };
}

// ─── Draft autosave (per Brightivity user). Desktop writes the draft to a file, so size never matters; a poster
// with photos is easily over localStorage's ~5 MB, so drafts live in IndexedDB (hundreds of MB). localStorage stays
// as the fallback where IndexedDB is blocked. Never fatal.
const IDB_NAME = 'bv-editor';
const IDB_STORE = 'drafts';
function idb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no IndexedDB')); return; }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbDo(mode, fn) {
  const db = await idb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, mode);
      const req = fn(tx.objectStore(IDB_STORE));
      tx.oncomplete = () => resolve(req && req.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}
export async function editorDraftSave(payload) {
  try {
    await idbDo('readwrite', (st) => st.put(payload, draftKey()));
    try { localStorage.removeItem(draftKey()); } catch { /* older copy */ }
    return { success: true };
  } catch {
    try {
      localStorage.setItem(draftKey(), JSON.stringify(payload));
      return { success: true };
    } catch (err) {
      try { localStorage.removeItem(draftKey()); } catch { /* storage blocked */ }
      return { success: false, error: err.message };
    }
  }
}
export async function editorDraftLoad() {
  try {
    const d = await idbDo('readonly', (st) => st.get(draftKey()));
    if (d) return { success: true, draft: d };
  } catch { /* fall back */ }
  try {
    const raw = localStorage.getItem(draftKey());
    return { success: true, draft: raw ? JSON.parse(raw) : null };
  } catch {
    return { success: true, draft: null };
  }
}
export async function editorDraftClear() {
  try { await idbDo('readwrite', (st) => st.delete(draftKey())); } catch { /* nothing to clear */ }
  try { localStorage.removeItem(draftKey()); } catch { /* nothing to clear */ }
  return { success: true };
}

/** Remove background: PNG/JPEG data URL in → `{ success, dataUrl }` (transparent PNG) out. */
export async function removeBackground({ dataUrl }) {
  const token = authToken();
  if (!token) return { success: false, error: 'NO_TOKEN', message: 'Not signed in' };
  const blob = dataUrlToBlob(dataUrl);
  if (!blob) return { success: false, error: 'INVALID_DATA_URL', message: 'Unsupported image data' };
  try {
    const res = await fetch(`${API_BASE}/design/remove-bg`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': blob.type || 'image/png' }, body: blob });
    if (!res.ok) {
      const j = await res.json().catch(() => null);
      return { success: false, error: j?.error || 'REMOVE_BG_FAILED', message: j?.message || `Remove background failed (${res.status})` };
    }
    const out = await res.blob();
    const url = await new Promise((resolve, reject) => { const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.onerror = reject; fr.readAsDataURL(out); });
    return { success: true, dataUrl: url };
  } catch (err) {
    return { success: false, error: 'REMOVE_BG_FAILED', message: err.message };
  }
}

export async function fontCacheGet() { return null; }
export async function fontCachePut() { return undefined; }

export default {
  saveDesign, updateDesign, editorDraftSave, editorDraftLoad, editorDraftClear, removeBackground, fontCacheGet, fontCachePut,
};
